import React from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { AlertMessage } from '@/components/ui/AlertMessage';
import { useEmailVerification } from '@/hooks/email';
import { usePasswordValidation } from '@/hooks/common';

interface SignupFormProps {
  customerId?: string;
  email: string;
  name: string;
  companyName: string;
  password: string;
  passwordConfirm: string;
  loading: boolean;
  error?: string;
  success?: string;
  onEmailChange: (value: string) => void;
  onNameChange: (value: string) => void;
  onCompanyNameChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onPasswordConfirmChange: (value: string) => void;
  onSignup: (e: React.FormEvent) => void;
  onGoogleSignup: () => void;
  onBack: () => void;
  onLoginClick: () => void;
}

export const SignupForm: React.FC<SignupFormProps> = ({
  customerId,
  email,
  name,
  companyName,
  password,
  passwordConfirm,
  loading,
  error,
  success,
  onEmailChange,
  onNameChange,
  onCompanyNameChange,
  onPasswordChange,
  onPasswordConfirmChange,
  onSignup,
  onGoogleSignup,
  onBack,
  onLoginClick,
}) => {
  const {
    email: emailState,
    verification,
    actions: verificationActions,
    formatCountdown,
  } = useEmailVerification(email);

  const { passwordStatus, passwordMessage } = usePasswordValidation(password);
  
  // 비밀번호 확인 검증
  const passwordMatch = password === passwordConfirm;
  const passwordConfirmMessage = passwordConfirm 
    ? (passwordMatch ? '비밀번호가 일치합니다.' : '비밀번호가 일치하지 않습니다.')
    : '';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailState.isVerified) {
      alert('이메일 인증을 먼저 완료해주세요.');
      return;
    }
    if (passwordStatus !== 'valid') {
      alert('비밀번호 조건을 확인해주세요.');
      return;
    }
    if (!passwordMatch) {
      alert('비밀번호가 일치하지 않습니다.');
      return;
    }
    onSignup(e);
  };

  return (
    <Card className="bg-white shadow-2xl border-0">
      <CardHeader className="pt-6">
        <CardTitle className="text-gray-900 text-xl">회원가입</CardTitle>
        <CardDescription className="text-gray-600">
          새 계정을 만들려면 이메일과 비밀번호를 입력하세요
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 pb-6">
        {error && (
          <AlertMessage 
            type="error" 
            message={error} 
            onClose={() => {}} 
          />
        )}
        {success && (
          <AlertMessage 
            type="success" 
            message={success} 
            onClose={() => {}} 
          />
        )}
        {customerId && (
          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-2 rounded text-sm flex items-center justify-between gap-2">
            <span className="whitespace-nowrap">고객사 코드: <strong>{customerId}</strong></span>
            <Button
              type="button"
              variant="link"
              className="h-auto p-0 text-blue-700 underline whitespace-nowrap flex-shrink-0"
              onClick={onBack}
            >
              변경
            </Button>
          </div>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-700">
            💡 신규 고객의 경우 회원가입 후 관리자의 승인이 필요합니다.
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="name" className="text-sm">이름 <span className="text-gray-400">(선택)</span></Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder="홍길동"
                disabled={loading}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="companyName" className="text-sm">회사명 <span className="text-gray-400">(선택)</span></Label>
              <Input
                id="companyName"
                type="text"
                value={companyName}
                onChange={(e) => onCompanyNameChange(e.target.value)}
                placeholder="젠아이랩스"
                disabled={loading}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="signup-email">이메일</Label>
            <div className="relative">
              <Input
                id="signup-email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => onEmailChange(e.target.value)}
                placeholder="이메일을 입력하세요"
                disabled={loading || emailState.isVerified}
                required
                className={
                  emailState.status === 'available'
                    ? 'border-green-500 border-b-2'
                    : emailState.status === 'unavailable'
                    ? 'border-red-500 border-b-2'
                    : emailState.status === 'checking'
                    ? 'border-gray-300 border-b-2'
                    : ''
                }
              />
              {emailState.status === 'checking' && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                  확인 중...
                </div>
              )}
              {emailState.status === 'available' && !emailState.isVerified && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-600 text-sm font-medium">
                  ✓ 사용 가능
                </div>
              )}
              {emailState.status === 'unavailable' && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-600 text-sm font-medium">
                  ✗ 이미 사용 중
                </div>
              )}
              {emailState.isVerified && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-600 text-sm font-medium">
                  ✓ 인증 완료
                </div>
              )}
            </div>
            {emailState.status === 'available' && !emailState.isVerified && (
              <div className="space-y-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={verificationActions.sendCode}
                  disabled={verification.isSending || verification.countdown > 0}
                  className="w-full"
                >
                  {verification.isSending
                    ? '발송 중...'
                    : verification.countdown > 0
                    ? `인증 코드 재발송 (${formatCountdown(verification.countdown)})`
                    : '이메일 인증하기'}
                </Button>
                {verification.countdown > 0 && (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        value={verification.code}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                          verification.setCode(value);
                        }}
                        placeholder="6자리 인증 코드"
                        maxLength={6}
                        disabled={verification.isVerifying || emailState.isVerified}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        onClick={verificationActions.verifyCode}
                        disabled={verification.isVerifying || verification.code.length !== 6 || emailState.isVerified}
                      >
                        {verification.isVerifying ? '확인 중...' : '확인'}
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500">
                      남은 시간: {formatCountdown(verification.countdown)}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="signup-password">비밀번호</Label>
            <Input
              id="signup-password"
              name="password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => onPasswordChange(e.target.value)}
              placeholder="비밀번호를 입력하세요"
              disabled={loading}
              required
              className={
                passwordStatus === 'valid'
                  ? 'border-green-500 border-b-2'
                  : passwordStatus === 'invalid'
                  ? 'border-red-500 border-b-2'
                  : ''
              }
            />
            {passwordMessage && (
              <p
                className={`text-xs ${
                  passwordStatus === 'valid' ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {passwordMessage}
              </p>
            )}
            <p className="text-xs text-gray-500">
              대소문자, 숫자, 특수문자 중 최소 2가지 이상 조합, 6자 이상 32자 이하
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="signup-password-confirm">비밀번호 확인</Label>
            <Input
              id="signup-password-confirm"
              name="passwordConfirm"
              type="password"
              autoComplete="new-password"
              value={passwordConfirm}
              onChange={(e) => onPasswordConfirmChange(e.target.value)}
              placeholder="비밀번호를 다시 입력하세요"
              disabled={loading}
              required
              className={
                passwordConfirm
                  ? passwordMatch
                    ? 'border-green-500 border-b-2'
                    : 'border-red-500 border-b-2'
                  : ''
              }
            />
            {passwordConfirmMessage && (
              <p
                className={`text-xs ${
                  passwordMatch ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {passwordConfirmMessage}
              </p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={loading || !emailState.isVerified || passwordStatus !== 'valid' || !passwordMatch}
          >
            {loading ? '처리 중...' : '가입 신청'}
          </Button>
        </form>
        
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">또는</span>
          </div>
        </div>
        
        <Button 
          type="button"
          onClick={onGoogleSignup}
          className="w-full bg-white text-gray-900 border border-gray-300 hover:bg-gray-50"
          disabled={loading}
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Google로 가입하기
        </Button>
          
        <div className="flex items-center justify-between">
          <Button 
            type="button" 
            variant="outline" 
            className="flex-1"
            onClick={onLoginClick}
            disabled={loading}
          >
            로그인으로 돌아가기
          </Button>
          
          <Button 
            type="button" 
            variant="link"
            className="text-sm"
            onClick={onBack}
            disabled={loading}
          >
            ← 이전
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
